// "Open in DesignPoint" for email attachments.
//
// DesignPoint is OnPoint's vector editor. An .eps, .ai or .svg attachment is
// artwork nobody can read in a browser tab — clicking one used to download it,
// to be opened in something else. Now the click hands the file to OnPoint and
// opens DesignPoint on it in a new tab, the way Build Order hands files to the
// Add Order page. A PDF keeps its click (most PDFs here are purchase orders and
// invoices, which people want to READ) and gets a DesignPoint button beside it.
//
// The server does the hand-off (POST …/attachments/:aid/designpoint → the bot →
// OnPoint's /api/internal/designpoint/prepare-open) and answers with the
// address to open. This module decides which attachments get which treatment
// and does the new-tab dance.

// What a click on the attachment does:
//   'click'  — opens DesignPoint (there is nothing useful to show in a tab)
//   'button' — the click is unchanged; a DesignPoint button is offered as well
const KIND_BY_EXTENSION = {
  eps: 'click', ai: 'click', ait: 'click', svg: 'click', svgz: 'click',
  pdf: 'button',
};

// Every format DesignPoint's editor opens — what the menu's "DesignPoint" item
// works for. The same list as the server's (bot/src/mail/designPointOpen.js)
// and OnPoint's (DesignPointHandoff::EXTENSIONS).
const OPENABLE = new Set([
  // Vector artwork and PDFs
  'pdf', 'ai', 'ait', 'eps', 'svg', 'svgz',
  // Photos and images
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'tif', 'tiff', 'bmp',
  // Photoshop, CAD, Windows metafiles, Affinity
  'psd', 'psb', 'dxf', 'emf', 'wmf', 'af', 'afdesign', 'afpub',
  // DesignPoint's own working files
  'vectorcraft', 'drawcraft', 'vctemplate',
]);

export function extensionOf(name) {
  const text = String(name || '');
  const dot = text.lastIndexOf('.');
  return dot > 0 && dot < text.length - 1 ? text.slice(dot + 1).toLowerCase() : '';
}

/** Can DesignPoint open this attachment at all? (The menu item works for these.) */
export function canOpenInDesignPoint(att) {
  return OPENABLE.has(extensionOf(att && att.name));
}

/** 'click' | 'button' | null for an attachment, by its file name. */
export function designPointKind(att) {
  return KIND_BY_EXTENSION[extensionOf(att && att.name)] || null;
}

const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/**
 * Hand an attachment to OnPoint and open DesignPoint on it in a new tab.
 *
 * The tab is opened NOW, inside the click, and pointed at OnPoint once the
 * hand-off answers: a tab opened after an await has lost the click's
 * permission and the popup blocker eats it (the same reason Build Order opens
 * its tab first). If the hand-off fails the tab is closed again and the reason
 * is thrown for the caller to show.
 *
 * @param {object} options
 * @param {string} options.url   POST address of the hand-off for this attachment
 * @param {string} [options.name] the file's name, shown while it opens
 * @returns {Promise<string>} the OnPoint address the tab was sent to
 */
export async function openInDesignPoint({ url, name = '', openWindow = (...a) => window.open(...a), fetchImpl = (...a) => fetch(...a) }) {
  const tab = openWindow('', '_blank');
  if (tab && tab.document) {
    try {
      tab.document.title = 'Opening in DesignPoint…';
      tab.document.body.innerHTML =
        `<p style="font:14px system-ui,sans-serif;color:#444;padding:24px">Opening ${escapeHtml(name || 'the file')} in DesignPoint…</p>`;
    } catch { /* a blank tab is fine too */ }
  }
  try {
    const res = await fetchImpl(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.ok || !data.open_url) {
      throw new Error(data.error || `HTTP ${res.status}`);
    }
    if (tab) {
      // The page it lands on is OnPoint's; it has no business with this one.
      try { tab.opener = null; } catch { /* not settable everywhere */ }
      tab.location.href = data.open_url;
    } else {
      // The browser refused the tab outright (a strict popup setting): go there
      // in a new one the ordinary way, which a direct call may still be allowed.
      openWindow(data.open_url, '_blank', 'noopener');
    }
    return data.open_url;
  } catch (err) {
    if (tab) { try { tab.close(); } catch { /* already gone */ } }
    throw err;
  }
}
